// src/pages/dashboard/DashboardObserver.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  FileText,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Eye,
  Download,
  Building2,
  BarChart3,
  Clock,
  Hourglass,
  ChevronRight,
  PieChart as PieChartIcon,
  Activity,
  Target,
  AlertCircle,
  HelpCircle,
  Info,
  Search,
  XCircle,
} from 'lucide-react';
import {
  Card, Badge, Breadcrumb, ProgressBar, Select, Loading, Button, Modal, Input,
} from '../../components/ui';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import api from '../../services/api';
import { TingkatRisiko } from '../../types';
import { UnitPelaporPerformance, UnitPicPerformance } from '../../services/monitoring';
import { useRole } from '../../hooks/useRole';
import { useAuthStore } from '../../store/authStore';

// ========== CONSTANTS ==========
const STATUS_COLORS: Record<string, string> = {
  draf: '#94A3B8',
  diajukan: '#3B82F6',
  validasi_l1: '#06B6D4',
  validasi_l2: '#A855F7',
  antrean_rapim: '#F97316',
  berlangsung: '#EAB308',
  menunggu_konfirmasi: '#F59E0B',
  selesai: '#10B981',
  ditolak_l1: '#EF4444',
  ditolak_l2: '#DC2626',
};

const RISK_COLORS: Record<string, string> = {
  rendah: '#22C55E',
  sedang: '#F59E0B',
  tinggi: '#F97316',
  kritis: '#EF4444',
};

const KATEGORI_COLORS = [
  '#0f766e', '#14B8A6', '#3B82F6', '#8B5CF6',
  '#F59E0B', '#EF4444', '#06B6D4', '#22C55E',
];

// ========== TYPES ==========
interface DashboardData {
  total_laporan: number;
  laporan_per_status: Record<string, number>;
  laporan_overdue: number;
  rata_rata_penyelesaian: number;
  laporan_per_risiko: Record<string, number>;
  progress_ppepp: number;
  resolved_local_count: number;
  tren_bulanan: Array<{
    label: string;
    laporan_masuk: number;
    laporan_selesai: number;
    selesai_rapim: number;
    ditangani_langsung: number;
    dalam_proses: number;
  }>;
  laporan_per_kategori: Array<{ kategori: string; jumlah: number; color?: string }>;
  overdue_list: Array<{
    kode: string;
    judul: string;
    unit: string;
    risiko: string;
    hari: number;
    hari_terlambat?: number;
  }>;
}

interface Periode {
  id: number;
  nama: string;
  is_active?: boolean;
}

interface AuditReport {
  id: number;
  kode: string;
  judul: string;
  status: string;
  tingkat_risiko?: string;
  resolution_level?: string | null;
  unit?: { name?: string | null } | null;
  user?: { name?: string | null } | null;
  target_efektif?: string | null;
  target_selesai?: string | null;
  created_at?: string | null;
  completed_at?: string | null;
  resolved_at?: string | null;
}

interface AuditMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

// ========== HELPER FUNCTIONS ==========
const toRiskLevel = (risk: string | undefined): TingkatRisiko => {
  const validRisks: TingkatRisiko[] = ['rendah', 'sedang', 'tinggi', 'kritis'];
  const normalized = (risk || 'sedang').toLowerCase() as TingkatRisiko;
  return validRisks.includes(normalized) ? normalized : 'sedang';
};

const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    draf: 'Draf',
    diajukan: 'Menunggu Persetujuan',
    validasi_l1: 'Disetujui Dekan',
    validasi_l2: 'Disetujui Wakil Rektor',
    ditolak_l1: 'Dikembalikan (Dekan)',
    ditolak_l2: 'Dikembalikan (Wakil Rektor)',
    antrean_rapim: 'Antrean Rapat Pimpinan',
    diputuskan: 'Keputusan Rapat Pimpinan',
    berlangsung: 'Tindak Lanjut Aktif',
    menunggu_konfirmasi: 'Menunggu Konfirmasi Selesai',
    selesai: 'Selesai',
    diarsipkan: 'Diarsipkan',
    diselesaikan_lokal: 'Ditangani Langsung Pimpinan',
  };
  return labels[status] || status;
};

const formatResolutionLabel = (level?: string | null): string | null => {
  if (!level) return null;

  const labels: Record<string, string> = {
    universitas: 'Level Universitas',
    fakultas: 'Level Fakultas',
    lembaga: 'Level Lembaga',
    prodi: 'Level Program Studi',
    unit: 'Level Unit',
    lokal: 'Level Lokal',
  };

  return labels[level] || level.replace(/_/g, ' ');
};

const formatShortDate = (value?: string | null): string => {
  if (!value) return '-';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';

  return parsed.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const InfoTooltipButton: React.FC<{
  title: string;
  content: string;
  align?: 'left' | 'right';
}> = ({ title, content, align = 'right' }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
        aria-label={title}
        aria-expanded={open}
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Tutup penjelasan"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <div
            className={`absolute top-full mt-2 z-20 w-80 max-w-[min(20rem,calc(100vw-2rem))] rounded-2xl border border-blue-100 bg-white p-3 shadow-xl ${align === 'left' ? 'left-0' : 'right-0'
              }`}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-blue-700">{title}</p>
            <p className="mt-1.5 text-xs leading-relaxed text-neutral-text-secondary">{content}</p>
          </div>
        </>
      )}
    </div>
  );
};

// ========== COMPONENT ==========
const DashboardObserver: React.FC = () => {
  const roleInfo = useRole();
  const user = useAuthStore((s) => s.user);
  // States
  const [loading, setLoading] = useState(true);
  const [dashData, setDashData] = useState<DashboardData | null>(null);
  const [unitPelaporPerformance, setUnitPelaporPerformance] = useState<UnitPelaporPerformance[]>([]);
  const [unitPicPerformance, setUnitPicPerformance] = useState<UnitPicPerformance[]>([]);
  const [periodeList, setPeriodeList] = useState<Periode[]>([]);
  const [selectedPeriode, setSelectedPeriode] = useState<string>('');
  const [isPeriodeReady, setIsPeriodeReady] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditReports, setAuditReports] = useState<AuditReport[]>([]);
  const [auditMeta, setAuditMeta] = useState<AuditMeta | null>(null);
  const [auditPage, setAuditPage] = useState(1);
  const [auditSearchDraft, setAuditSearchDraft] = useState('');
  const [auditSearch, setAuditSearch] = useState('');
  const [auditStatusFilter, setAuditStatusFilter] = useState('');
  const userUnitName = user?.unit?.name || 'unit Anda';

  const unitExplanation = useMemo(() => {
    if (roleInfo.isRektor || roleInfo.isAdmin || roleInfo.isObserver || roleInfo.canManageRapim) {
      return {
        pelapor: {
          subtitle: 'Berdasarkan unit asal laporan',
          helperTitle: 'Cara membaca aktivitas pelaporan',
          helper:
            'Bagian ini menunjukkan dari unit mana laporan berasal. Jika tindak lanjutnya dikerjakan oleh PIC dari unit lain, laporan tetap tercatat pada unit asal agar sumber laporannya tetap jelas.',
        },
        pic: {
          subtitle: 'Berdasarkan unit yang mengerjakan sebagai PIC',
          helperTitle: 'Cara membaca kinerja tindak lanjut',
          helper:
            'Bagian ini menunjukkan unit yang menerima tugas tindak lanjut sebagai PIC. Karena PIC bisa ditunjuk dari unit lain, nama unit yang tampil di sini bisa berbeda dari unit asal laporannya.',
        },
      };
    }

    if (roleInfo.isWarek) {
      return {
        pelapor: {
          subtitle: 'Berdasarkan unit asal laporan pada bidang yang Anda pantau',
          helperTitle: 'Cara membaca aktivitas pelaporan bidang',
          helper:
            'Bagian ini membantu melihat dari unit mana laporan pada bidang Anda berasal. Jika tindak lanjutnya kemudian dikerjakan oleh PIC dari unit lain yang masih terkait, laporan tetap tercatat pada unit asalnya.',
        },
        pic: {
          subtitle: 'Berdasarkan unit PIC pada bidang yang Anda pantau',
          helperTitle: 'Cara membaca tindak lanjut bidang',
          helper:
            'Bagian ini membantu melihat unit mana yang benar-benar mengerjakan tindak lanjut pada bidang Anda. Karena PIC bisa ditunjuk dari unit lain, unit yang tampil dapat berbeda dari unit asal laporannya.',
        },
      };
    }

    return {
      pelapor: {
        subtitle: `Berdasarkan laporan yang berasal dari ${userUnitName} atau lingkup Anda`,
        helperTitle: 'Cara membaca aktivitas pelaporan unit',
        helper:
          `Bagian ini tetap menghitung unit asal laporan. Jadi jika laporan dari ${userUnitName} ditindaklanjuti oleh PIC dari unit lain, laporan tersebut tetap tercatat pada unit asalnya.`,
      },
      pic: {
        subtitle: `Berdasarkan unit PIC yang mengerjakan laporan di ${userUnitName} atau lingkup Anda`,
        helperTitle: 'Cara membaca tindak lanjut unit',
        helper:
          `Bagian ini mengikuti unit yang ditunjuk sebagai PIC. Karena itu, unit lain bisa tampil di sini jika laporan dari ${userUnitName} dikerjakan oleh PIC dari unit tersebut.`,
      },
    };
  }, [roleInfo, userUnitName]);

  useEffect(() => {
    const fetchPeriode = async () => {
      try {
        const periodeRes = await api.get('/admin/periode').catch(() => ({ data: { data: [] } }));
        const periodeData = periodeRes.data?.data || periodeRes.data || [];
        const normalizedPeriode = Array.isArray(periodeData) ? periodeData : [];
        setPeriodeList(normalizedPeriode);

        const activePeriode = normalizedPeriode.find((p: Periode) => p.is_active);
        setSelectedPeriode(activePeriode ? String(activePeriode.id) : '');
      } catch {
        setPeriodeList([]);
        setSelectedPeriode('');
      } finally {
        setIsPeriodeReady(true);
      }
    };

    fetchPeriode();
  }, []);

  // ========== FETCH DATA ==========
  useEffect(() => {
    if (!isPeriodeReady) {
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        const params: Record<string, any> = { context: 'monitoring' };
        if (selectedPeriode) params.periode_id = selectedPeriode;

        const [dashRes, unitPelaporRes, unitPicRes] = await Promise.all([
          api.get('/dashboard', { params }),
          api.get('/monitoring/unit-performance', { params: { ...params, perspective: 'pelapor' } }).catch(() => ({ data: { data: [] } })),
          api.get('/monitoring/unit-performance', { params: { ...params, perspective: 'pic' } }).catch(() => ({ data: { data: [] } })),
        ]);

        // Parse dashboard data
        const d = dashRes.data?.data || dashRes.data;
        if (d) {
          setDashData({
            total_laporan: d.total_laporan || 0,
            laporan_per_status: d.laporan_per_status || {},
            laporan_overdue: d.laporan_overdue || 0,
            rata_rata_penyelesaian: d.rata_rata_penyelesaian || 0,
            laporan_per_risiko: d.laporan_per_risiko || {},
            progress_ppepp: d.progress_ppepp || 0,
            resolved_local_count: d.resolved_local_count || 0,
            tren_bulanan: d.tren_bulanan || [],
            laporan_per_kategori: d.laporan_per_kategori || [],
            overdue_list: d.overdue_list || [],
          });
        }

        const pelaporData = unitPelaporRes.data?.data || unitPelaporRes.data;
        const picData = unitPicRes.data?.data || unitPicRes.data;
        setUnitPelaporPerformance(Array.isArray(pelaporData) ? pelaporData : []);
        setUnitPicPerformance(Array.isArray(picData) ? picData : []);

      } catch (err) {
        console.error('Failed to load LPM dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedPeriode, isPeriodeReady]);

  useEffect(() => {
    if (!showAuditModal) {
      return;
    }

    let isCancelled = false;

    const fetchAuditReports = async () => {
      try {
        setAuditLoading(true);

        const params: Record<string, any> = {
          page: auditPage,
          per_page: 8,
        };

        if (selectedPeriode) params.periode_id = selectedPeriode;
        if (auditStatusFilter) params.status = auditStatusFilter;
        if (auditSearch.trim()) params.search = auditSearch.trim();

        const res = await api.get('/laporan', { params });
        if (isCancelled) {
          return;
        }

        const payload = res.data || {};
        const items = Array.isArray(payload.data)
          ? payload.data
          : Array.isArray(payload.data?.data)
            ? payload.data.data
            : [];
        const meta = payload.meta || payload.data?.meta || null;

        setAuditReports(items);
        setAuditMeta(meta);
      } catch (err) {
        if (!isCancelled) {
          console.error('Failed to load audit reports:', err);
          setAuditReports([]);
          setAuditMeta(null);
        }
      } finally {
        if (!isCancelled) {
          setAuditLoading(false);
        }
      }
    };

    fetchAuditReports();

    return () => {
      isCancelled = true;
    };
  }, [showAuditModal, auditPage, auditSearch, auditStatusFilter, selectedPeriode]);

  // ========== COMPUTED DATA ==========

  // Status pie data - exclude draft dan arsip untuk fokus pada yang aktif
  const statusPieData = Object.entries(dashData?.laporan_per_status || {})
    .filter(([key]) => !['draf', 'diarsipkan', 'tugas_ppepp_aktif'].includes(key))
    .filter(([, value]) => value > 0)
    .map(([key, value]) => ({
      name: getStatusLabel(key),
      value,
      fill: STATUS_COLORS[key] || '#9CA3AF',
    }));

  // Risk pie data
  const riskPieData = Object.entries(dashData?.laporan_per_risiko || {})
    .filter(([, value]) => value > 0)
    .map(([key, value]) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      value,
      fill: RISK_COLORS[key] || '#9CA3AF',
    }));

  // Kategori chart data
  const kategoriChartData = (dashData?.laporan_per_kategori || []).map((item, idx) => ({
    name: item.kategori,
    jumlah: item.jumlah,
    fill: item.color || KATEGORI_COLORS[idx % KATEGORI_COLORS.length],
  }));

  // KPI calculations
  const statusCounts = dashData?.laporan_per_status || {};
  const totalLaporan = dashData?.total_laporan || 0;
  const totalSelesaiRaw = statusCounts.selesai || 0;
  const totalDiarsipkan = statusCounts.diarsipkan || 0;
  const totalSelesai = totalSelesaiRaw + totalDiarsipkan;
  const resolvedLocalCount = dashData?.resolved_local_count || 0;
  const totalOverdue = dashData?.laporan_overdue || 0;
  const menungguKonfirmasi = statusCounts.menunggu_konfirmasi || 0;
  const rataRataPenyelesaian = dashData?.rata_rata_penyelesaian || 0;
  const rapimPipelineTotal = [
    statusCounts.antrean_rapim || 0,
    statusCounts.diputuskan || 0,
    statusCounts.berlangsung || 0,
    statusCounts.menunggu_konfirmasi || 0,
    totalSelesaiRaw,
    totalDiarsipkan,
  ].reduce((acc, current) => acc + current, 0);
  const progressPpepp = rapimPipelineTotal > 0
    ? Math.round(((totalSelesaiRaw + totalDiarsipkan) / rapimPipelineTotal) * 100)
    : 0;

  // In progress count
  const inProgressStatuses = ['diajukan', 'validasi_l1', 'validasi_l2', 'antrean_rapim', 'diputuskan', 'berlangsung'];
  const inProgressCount = Object.entries(statusCounts)
    .filter(([k]) => inProgressStatuses.includes(k))
    .reduce((acc, [, v]) => acc + v, 0);

  // Top overdue
  const topOverdue = (dashData?.overdue_list || []).slice(0, 5);

  const sortedPelaporPerformance = [...unitPelaporPerformance]
    .map((u) => ({
      ...u,
      completion_rate: u.total > 0 ? Math.round((u.selesai / u.total) * 100) : 0,
    }))
    .sort((a, b) => b.completion_rate - a.completion_rate);

  const sortedPicPerformance = [...unitPicPerformance]
    .map((u) => ({
      ...u,
      completion_rate: u.total > 0 ? Math.round((u.selesai / u.total) * 100) : 0,
    }))
    .sort((a, b) => b.completion_rate - a.completion_rate);

  const totalTrackedUnits = new Set([
    ...unitPelaporPerformance.map((item) => item.unit_id),
    ...unitPicPerformance.map((item) => item.unit_id),
  ]).size;

  // Periode options
  const periodeOptions = [
    { value: '', label: 'Semua Periode' },
    ...periodeList.map((p) => ({
      value: String(p.id),
      label: `${p.nama}${p.is_active ? ' (Aktif)' : ''}`,
    })),
  ];
  const selectedPeriodeLabel = selectedPeriode
    ? periodeList.find((periode) => String(periode.id) === selectedPeriode)?.nama || 'Periode dipilih'
    : 'Semua Periode';
  const auditStatusOptions = [
    { value: '', label: 'Semua Status' },
    { value: 'diajukan', label: 'Menunggu Persetujuan' },
    { value: 'validasi_l1', label: 'Validasi Level 1' },
    { value: 'validasi_l2', label: 'Validasi Level 2' },
    { value: 'antrean_rapim', label: 'Antrean RAPIM' },
    { value: 'diputuskan', label: 'Sudah Diputuskan' },
    { value: 'berlangsung', label: 'Tindak Lanjut Aktif' },
    { value: 'menunggu_konfirmasi', label: 'Menunggu Konfirmasi' },
    { value: 'diselesaikan_lokal', label: 'Ditangani Langsung' },
    { value: 'selesai', label: 'Selesai' },
    { value: 'diarsipkan', label: 'Diarsipkan' },
  ];
  const openAuditModal = (status = '') => {
    setAuditPage(1);
    setAuditSearchDraft('');
    setAuditSearch('');
    setAuditStatusFilter(status);
    setShowAuditModal(true);
  };

  // ========== LOADING STATE ==========
  if (loading) {
    return <Loading variant="page" message="Memuat data dashboard..." />;
  }

  // ========== RENDER ==========
  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Dashboard' }, { label: 'Pengamat' }]} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">
            Dashboard Pengamat
          </h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            <Shield className="w-4 h-4 inline mr-1" />
            Mode Pengamat — Akses Baca Seluruh Data
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            icon={<HelpCircle className="w-4 h-4" />}
            onClick={() => setShowGuide((prev) => !prev)}
          >
            Panduan
          </Button>
          <Select
            options={periodeOptions}
            value={selectedPeriode}
            onChange={setSelectedPeriode}
            className="w-full sm:w-56"
          />
          <Link to="/reports?action=export">
            <button className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-neutral-border text-sm font-semibold rounded-lg hover:bg-neutral-bg transition-all">
              <Download className="w-4 h-4" /> Export Data
            </button>
          </Link>
        </div>
      </div>

      {showGuide && (
        <Card className="border border-teal-main/10 bg-gradient-to-r from-white via-white to-teal-main/5 animate-in">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-xl bg-teal-main/10 shrink-0">
                <Info className="w-5 h-5 text-teal-main" />
              </div>
              <div>
                <h2 className="text-base font-bold text-teal-dark">Cara membaca dashboard Pengamat</h2>
                <p className="text-xs text-neutral-text-secondary mt-1">
                  Panduan singkat agar Observer dapat membaca KPI, grafik, dan daftar audit secara konsisten tanpa salah menafsirkan angka.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowGuide(false)}
              className="text-neutral-text-secondary hover:text-teal-main transition-colors shrink-0"
              aria-label="Tutup panduan dashboard Pengamat"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Masih Berjalan</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Menunjukkan seluruh laporan yang masih berada dalam jalur aktif, mulai dari pengajuan, validasi, antrean RAPIM, keputusan, hingga tahap pelaksanaan tindak lanjut.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Tuntas via RAPIM / Arsip</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Mencakup laporan yang sudah melalui pembahasan RAPIM, ditindaklanjuti oleh PIC melalui jalur PPEPP, kemudian dinyatakan selesai atau diarsipkan sebagai penutupan proses.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Ditangani Langsung</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Menampilkan laporan yang dituntaskan langsung oleh pimpinan yang berwenang tanpa perlu dibawa ke RAPIM, sehingga jalurnya terpisah dari PPEPP.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Progress Tindak Lanjut (PPEPP)</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Mengukur ketuntasan tindak lanjut khusus untuk laporan yang sudah masuk RAPIM dan berjalan dalam jalur PPEPP. Laporan yang ditangani langsung tidak dihitung di persentase ini.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Audit Semua Laporan</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Tombol audit akan membuka modal laporan periode aktif, sehingga Observer dapat meninjau seluruh laporan tanpa berpindah ke daftar laporan pelapor.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Observer Mode Banner */}
      <div className="p-3 bg-info/5 border border-info/20 rounded-xl flex items-center gap-3">
        <Eye className="w-5 h-5 text-info shrink-0" />
        <p className="text-sm text-info">
          <span className="font-semibold">Mode Pengamat:</span> Anda memiliki akses
          baca ke seluruh data evaluasi mutu tanpa dapat melakukan perubahan.
        </p>
      </div>

      {/* Alert: Overdue & Menunggu Konfirmasi */}
      {(totalOverdue > 0 || menungguKonfirmasi > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {totalOverdue > 0 && (
            <div className="p-3 bg-danger/5 border border-danger/20 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-danger shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-danger">
                  {totalOverdue} laporan melewati deadline
                </p>
              </div>
              <Link
                to="/monitoring?open=overdue"
                className="text-xs font-semibold text-danger hover:underline shrink-0"
              >
                Lihat →
              </Link>
            </div>
          )}
          {menungguKonfirmasi > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
              <Hourglass className="w-5 h-5 text-amber-600 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-700">
                  {menungguKonfirmasi} menunggu konfirmasi pimpinan
                </p>
              </div>
              <Link
                to="/laporan?status=menunggu_konfirmasi"
                className="text-xs font-semibold text-amber-700 hover:underline shrink-0"
              >
                Lihat →
              </Link>
            </div>
          )}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          {
            title: 'Total Laporan',
            value: totalLaporan,
            icon: <FileText className="w-5 h-5" />,
            color: 'text-teal-main',
            bgIcon: 'bg-teal-main/10',
          },
          {
            title: 'Masih Berjalan',
            value: inProgressCount,
            icon: <Clock className="w-5 h-5" />,
            color: 'text-warning',
            bgIcon: 'bg-warning/10',
          },
          {
            title: 'Menunggu Konfirmasi',
            value: menungguKonfirmasi,
            icon: <Hourglass className="w-5 h-5" />,
            color: 'text-amber-600',
            bgIcon: 'bg-amber-100',
          },
          {
            title: 'Tuntas via RAPIM / Arsip',
            value: totalSelesai,
            icon: <CheckCircle className="w-5 h-5" />,
            color: 'text-success',
            bgIcon: 'bg-success/10',
          },
          {
            title: 'Terlambat',
            value: totalOverdue,
            icon: <AlertTriangle className="w-5 h-5" />,
            color: 'text-danger',
            bgIcon: 'bg-danger/10',
          },
          {
            title: 'Ditangani Langsung',
            value: resolvedLocalCount,
            icon: <Shield className="w-5 h-5" />,
            color: 'text-info',
            bgIcon: 'bg-info/10',
          },
        ].map((kpi, i) => (
          <Card key={i}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                  {kpi.title}
                </p>
                <p className={`text-2xl font-black mt-1 ${kpi.color}`}>
                  {kpi.value}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${kpi.bgIcon} ${kpi.color}`}>
                {kpi.icon}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Additional KPIs Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-purple-100">
              <Activity className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Progress Tindak Lanjut (PPEPP)
              </p>
              <p className="text-2xl font-black text-purple-600">{progressPpepp}%</p>
              <p className="text-xs text-neutral-text-secondary mt-1">
                Hanya laporan yang sudah masuk RAPIM dan berjalan di jalur PPEPP
              </p>
            </div>
          </div>
          <div className="mt-3">
            <ProgressBar value={progressPpepp} color="teal" size="sm" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-info/10">
              <Target className="w-6 h-6 text-info" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Rata-rata Penyelesaian
              </p>
              <p className="text-2xl font-black text-info">{rataRataPenyelesaian} hari</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-success/10">
              <Building2 className="w-6 h-6 text-success" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Unit Terpantau
              </p>
              <p className="text-2xl font-black text-success">{totalTrackedUnits}</p>
              <p className="text-xs text-neutral-text-secondary mt-1">
                Gabungan unit pelapor dan unit PIC yang muncul pada periode ini
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Charts Row 1: Status Distribution + Risk Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
              <PieChartIcon className="w-5 h-5" /> Sebaran Status Laporan
            </h3>
            <button
              type="button"
              onClick={() => openAuditModal()}
              className="text-xs text-teal-main hover:underline flex items-center gap-1"
            >
              Lihat Detail <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          {statusPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={statusPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {statusPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} laporan`, '']} />
                <Legend
                  verticalAlign="bottom"
                  iconSize={8}
                  formatter={(value: string) => (
                    <span className="text-[10px] text-neutral-text-secondary">{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-neutral-text-secondary">
              Belum ada data status
            </div>
          )}
        </Card>

        {/* Risk Distribution */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Sebaran Tingkat Risiko
            </h3>
          </div>
          {riskPieData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={riskPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {riskPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} laporan`, '']} />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {riskPieData.map((item, i) => {
                  const percentage =
                    totalLaporan > 0 ? Math.round((item.value / totalLaporan) * 100) : 0;
                  return (
                    <div key={i} className="flex items-center justify-between p-2 bg-neutral-bg rounded-lg">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.fill }}
                        />
                        <span className="text-xs font-medium">{item.name}</span>
                      </div>
                      <span className="text-xs font-bold">
                        {item.value} ({percentage}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-neutral-text-secondary">
              Belum ada data risiko
            </div>
          )}
        </Card>
      </div>

      {/* Charts Row 2: Trend + Kategori */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trend Bulanan */}
        <Card>
          <h3 className="text-base font-bold text-teal-dark mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" /> Tren Laporan dan Penyelesaian 6 Bulan Terakhir
          </h3>
          {(dashData?.tren_bulanan || []).length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={dashData?.tren_bulanan}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6B7280' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #E5E7EB',
                      borderRadius: '12px',
                      fontSize: '11px',
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Line
                    type="monotone"
                    dataKey="laporan_masuk"
                    stroke="#0f33ab"
                    strokeWidth={2.5}
                    dot={{ fill: '#0f33ab', r: 4 }}
                    name="Laporan Masuk"
                  />
                  <Line
                    type="monotone"
                    dataKey="selesai_rapim"
                    stroke="#10B981"
                    strokeWidth={2.5}
                    dot={{ fill: '#10B981', r: 3.5 }}
                    name="Tuntas via RAPIM"
                  />
                  <Line
                    type="monotone"
                    dataKey="ditangani_langsung"
                    stroke="#F59E0B"
                    strokeWidth={2}
                    dot={{ fill: '#F59E0B', r: 3 }}
                    name="Ditangani Langsung"
                  />
                </LineChart>
              </ResponsiveContainer>
              <p className="mt-3 text-xs text-neutral-text-secondary leading-relaxed">
                Grafik ini memisahkan laporan yang masuk, laporan yang selesai melalui jalur RAPIM/PPEPP,
                dan laporan yang dituntaskan langsung oleh pimpinan tanpa perlu RAPIM.
              </p>
            </>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-neutral-text-secondary">
              Belum ada data tren
            </div>
          )}
        </Card>

        {/* Laporan per Kategori */}
        <Card>
          <h3 className="text-base font-bold text-teal-dark mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" /> Laporan per Kategori
          </h3>
          {kategoriChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={kategoriChartData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={100}
                  tick={{ fontSize: 10 }}
                />
                <Tooltip formatter={(value) => [`${value} laporan`, '']} />
                <Bar dataKey="jumlah" radius={[0, 6, 6, 0]}>
                  {kategoriChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-neutral-text-secondary">
              Belum ada data kategori
            </div>
          )}
        </Card>
      </div>

      {/* Overdue List + Unit Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Overdue List */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-danger" /> Laporan yang Melewati Batas Waktu
            </h3>
            {totalOverdue > 5 && (
              <Link
                to="/monitoring?open=overdue"
                className="text-xs text-teal-main hover:underline flex items-center gap-1"
              >
                Lihat Semua ({totalOverdue}) <ChevronRight className="w-3 h-3" />
              </Link>
            )}
          </div>
          <div className="space-y-2">
            {topOverdue.length > 0 ? (
              topOverdue.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 bg-danger/5 rounded-xl border border-danger/10"
                >
                  <div className="text-center shrink-0 w-12">
                    <p className="text-lg font-black text-danger leading-none">
                      {Math.abs(item.hari_terlambat || item.hari || 0)}
                    </p>
                    <p className="text-[9px] text-danger font-semibold">HARI TELAT</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-mono text-teal-main">{item.kode}</span>
                      <Badge variant="risk" risk={toRiskLevel(item.risiko)} />
                    </div>
                    <p className="text-sm font-medium truncate">{item.judul}</p>
                    <p className="text-xs text-neutral-text-secondary">{item.unit}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 text-success" />
                <p className="text-sm font-semibold text-success">Tidak ada laporan terlambat!</p>
                <p className="text-xs text-neutral-text-secondary mt-1">
                  Semua laporan dalam target waktu
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Unit Performance */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
                <Building2 className="w-5 h-5" /> Aktivitas Pelaporan per Unit
                <InfoTooltipButton
                  title={unitExplanation.pelapor.helperTitle}
                  content={unitExplanation.pelapor.helper}
                />
              </h3>
              <p className="text-xs text-neutral-text-secondary mt-1">
                {unitExplanation.pelapor.subtitle}
              </p>
            </div>
          </div>
          <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
            {sortedPelaporPerformance.length > 0 ? (
              sortedPelaporPerformance.map((u, i) => (
                <div key={i} className="p-3 bg-neutral-bg rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-bold truncate flex-1">{u.unit}</p>
                    <span
                      className={`text-sm font-bold ${u.completion_rate! >= 80
                        ? 'text-success'
                        : u.completion_rate! >= 50
                          ? 'text-warning'
                          : 'text-danger'
                        }`}
                    >
                      {u.completion_rate}%
                    </span>
                  </div>
                  <ProgressBar
                    value={u.completion_rate || 0}
                    color={
                      u.completion_rate! >= 80
                        ? 'success'
                        : u.completion_rate! >= 50
                          ? 'warning'
                          : 'danger'
                    }
                    size="sm"
                    showLabel={false}
                  />
                  <div className="flex items-center gap-4 mt-2 text-xs text-neutral-text-secondary">
                    <span>Total: {u.total}</span>
                    <span className="text-success">Selesai: {u.selesai}</span>
                    {u.proses > 0 && (
                      <span className="text-warning">Proses: {u.proses}</span>
                    )}
                    {u.overdue > 0 && (
                      <span className="text-danger">Terlambat: {u.overdue}</span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-neutral-text-secondary text-center py-4">
                Belum ada data aktivitas pelaporan unit
              </p>
            )}
          </div>
        </Card>
      </div>

      {/* Unit PIC Performance Bar Chart */}
      {sortedPicPerformance.length > 0 && (
        <Card>
          <div className="mb-4">
            <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
              <BarChart3 className="w-5 h-5" /> Perbandingan Kinerja Tindak Lanjut
              <InfoTooltipButton
                title={unitExplanation.pic.helperTitle}
                content={unitExplanation.pic.helper}
              />
            </h3>
            <p className="text-xs text-neutral-text-secondary mt-1">
              {unitExplanation.pic.subtitle}
            </p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={sortedPicPerformance.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="unit"
                tick={{ fontSize: 10 }}
                interval={0}
                angle={-20}
                textAnchor="end"
                height={60}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="selesai" name="Selesai" fill="#22C55E" radius={[4, 4, 0, 0]} />
              <Bar dataKey="menunggu_konfirmasi" name="Menunggu Konfirmasi" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              <Bar dataKey="terhambat" name="Terhambat" fill="#EF4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Quick Links for Observer */}
      <Card>
        <h3 className="text-base font-bold text-teal-dark mb-4">Akses Cepat Audit</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <button
            type="button"
            onClick={() => openAuditModal()}
            className="p-4 bg-neutral-bg rounded-xl hover:bg-teal-main/5 hover:border-teal-main/20 border border-transparent transition-all text-center"
          >
            <FileText className="w-6 h-6 mx-auto mb-2 text-teal-main" />
            <p className="text-xs font-semibold">Audit Semua Laporan</p>
            <p className="text-[11px] text-neutral-text-secondary mt-1">
              Periode {selectedPeriodeLabel}
            </p>
          </button>
          <Link to="/monitoring">
            <div className="p-4 bg-neutral-bg rounded-xl hover:bg-teal-main/5 hover:border-teal-main/20 border border-transparent transition-all text-center">
              <Activity className="w-6 h-6 mx-auto mb-2 text-info" />
              <p className="text-xs font-semibold">Monitoring</p>
            </div>
          </Link>
          <Link to="/reports">
            <div className="p-4 bg-neutral-bg rounded-xl hover:bg-teal-main/5 hover:border-teal-main/20 border border-transparent transition-all text-center">
              <BarChart3 className="w-6 h-6 mx-auto mb-2 text-purple-600" />
              <p className="text-xs font-semibold">Laporan & Analitik</p>
            </div>
          </Link>
          <Link to="/rapim">
            <div className="p-4 bg-neutral-bg rounded-xl hover:bg-teal-main/5 hover:border-teal-main/20 border border-transparent transition-all text-center">
              <Shield className="w-6 h-6 mx-auto mb-2 text-warning" />
              <p className="text-xs font-semibold">Riwayat RAPIM</p>
            </div>
          </Link>
        </div>
      </Card>

      <Modal
        isOpen={showAuditModal}
        onClose={() => setShowAuditModal(false)}
        title="Audit Semua Laporan"
        size="2xl"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setShowAuditModal(false)}>
              Tutup
            </Button>
          </>
        )}
      >
        <div className="space-y-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-teal-dark">
                Audit laporan periode {selectedPeriodeLabel}
              </p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Modal ini menampilkan daftar laporan dalam periode yang sedang dipilih di dashboard,
                sehingga Observer dapat meninjau status, risiko, dan detail laporan tanpa berpindah ke
                halaman daftar laporan pelapor.
              </p>
            </div>
            <Badge className="self-start" size="sm">
              {auditMeta?.total ?? 0} laporan
            </Badge>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl bg-neutral-bg border border-neutral-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Total Periode
              </p>
              <p className="text-xl font-black text-teal-main mt-1">{totalLaporan}</p>
            </div>
            <div className="p-3 rounded-xl bg-neutral-bg border border-neutral-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Masih Berjalan
              </p>
              <p className="text-xl font-black text-warning mt-1">{inProgressCount}</p>
            </div>
            <div className="p-3 rounded-xl bg-neutral-bg border border-neutral-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Tuntas via RAPIM
              </p>
              <p className="text-xl font-black text-success mt-1">{totalSelesai}</p>
            </div>
            <div className="p-3 rounded-xl bg-neutral-bg border border-neutral-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Ditangani Langsung
              </p>
              <p className="text-xl font-black text-info mt-1">{resolvedLocalCount}</p>
            </div>
          </div>

          <form
            className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_220px_auto_auto] gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              setAuditPage(1);
              setAuditSearch(auditSearchDraft.trim());
            }}
          >
            <Input
              placeholder="Cari kode laporan, judul, atau unit"
              value={auditSearchDraft}
              onChange={(event) => setAuditSearchDraft(event.target.value)}
              icon={<Search className="w-4 h-4" />}
            />
            <Select
              options={auditStatusOptions}
              value={auditStatusFilter}
              onChange={(value) => {
                setAuditPage(1);
                setAuditStatusFilter(value);
              }}
            />
            <Button type="submit" variant="outline">
              Cari
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setAuditPage(1);
                setAuditSearchDraft('');
                setAuditSearch('');
                setAuditStatusFilter('');
              }}
            >
              Reset
            </Button>
          </form>

          <div className="rounded-2xl border border-neutral-border overflow-hidden">
            <div className="px-4 py-3 bg-neutral-bg border-b border-neutral-border flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-neutral-text-primary">Daftar Audit Laporan</p>
                <p className="text-xs text-neutral-text-secondary mt-1">
                  Menampilkan hasil sesuai periode dashboard, filter status, dan kata kunci audit.
                </p>
              </div>
              {auditLoading && (
                <span className="text-xs text-neutral-text-secondary">Memuat data...</span>
              )}
            </div>

            <div className="max-h-[55vh] overflow-y-auto divide-y divide-neutral-border">
              {auditLoading ? (
                <div className="py-12">
                  <Loading message="Memuat laporan audit..." />
                </div>
              ) : auditReports.length > 0 ? (
                auditReports.map((report) => (
                  <div key={report.id} className="p-4 bg-white hover:bg-neutral-bg/60 transition-colors">
                    <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="text-xs font-mono font-semibold text-teal-main">
                            {report.kode}
                          </span>
                          <Badge
                            variant="status"
                            status={report.status}
                            size="xs"
                            useShortLabel
                          />
                          {report.tingkat_risiko && (
                            <Badge
                              variant="risk"
                              risk={toRiskLevel(report.tingkat_risiko)}
                              size="xs"
                            />
                          )}
                          {formatResolutionLabel(report.resolution_level) && (
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                              {formatResolutionLabel(report.resolution_level)}
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-bold text-neutral-text-primary">
                          {report.judul}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-text-secondary">
                          <span>Pelapor: {report.user?.name || '-'}</span>
                          <span>Unit: {report.unit?.name || '-'}</span>
                          <span>Target: {formatShortDate(report.target_efektif || report.target_selesai)}</span>
                          <span>Dibuat: {formatShortDate(report.created_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Link to={`/laporan/${report.id}`}>
                          <Button variant="outline" size="sm" icon={<Eye className="w-4 h-4" />}>
                            Lihat Detail
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-10 text-center">
                  <FileText className="w-10 h-10 mx-auto text-neutral-text-secondary mb-3" />
                  <p className="text-sm font-semibold text-neutral-text-primary">
                    Belum ada laporan yang cocok dengan filter audit
                  </p>
                  <p className="text-xs text-neutral-text-secondary mt-1">
                    Coba ubah periode, status, atau kata kunci pencarian untuk melihat laporan lain.
                  </p>
                </div>
              )}
            </div>
          </div>

          {auditMeta && auditMeta.last_page > 1 && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-neutral-text-secondary">
                Halaman {auditMeta.current_page} dari {auditMeta.last_page}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={auditMeta.current_page <= 1}
                  onClick={() => setAuditPage((prev) => Math.max(1, prev - 1))}
                >
                  Sebelumnya
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={auditMeta.current_page >= auditMeta.last_page}
                  onClick={() => setAuditPage((prev) => (
                    auditMeta ? Math.min(auditMeta.last_page, prev + 1) : prev + 1
                  ))}
                >
                  Berikutnya
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default DashboardObserver;
